import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Form, Select } from 'antd'
import { SelectProps } from 'antd/es/select'
import { isEqual } from 'lodash'
import styled from 'styled-components'
import LabelAndDescriptionFieldLayout from 'components/Assessments/components/FieldLayout/LabelAndDescriptionFieldLayout'
import QuestionContainer from 'components/Assessments/components/QuestionContainer'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import { useGenerateAnswerFromSectionResults } from 'components/Assessments/PatAssessment/hooks/useGenerateAnswerFromSectionResults'
import { getSectionReferenceId } from 'components/Assessments/components/helpers'
import { BaseQuestionProps, QuestionLayout } from 'components/Assessments/components/types'
import { CodingSelectOption, CodingValue } from './types'
import {
  buildFormRules,
  createSelectValueFromEvent,
  createSelectValueProps,
  extractSecondaryText,
  getFieldLinkId,
  getFieldText,
  isValidateFormValuesType
} from './utils'

export interface CodingSelectQuestionProps extends BaseQuestionProps {
  options: CodingSelectOption[]
  placeholder?: string
  loading?: boolean
  showSearch?: boolean
  allowClear?: boolean
  selectProps?: Omit<
    SelectProps,
    'options' | 'disabled' | 'placeholder' | 'loading' | 'showSearch' | 'allowClear'
  >
}

/**
 * Single select question component that handles CodingValue conversion
 * Converts between string codes (for Select display) and { code, display } objects (for form storage)
 */
function CodingSelectQuestion({
  field,
  formFieldName,
  layout = QuestionLayout.VERTICAL,
  disabled = false,
  required = false,
  customRules = [],
  options,
  placeholder = 'Type or select an option',
  loading = false,
  showSearch = true,
  allowClear = false,
  selectProps
}: CodingSelectQuestionProps): JSX.Element {
  const { form, triggerAutosave } = useContext(AssessmentFormContext)
  const secondaryText = extractSecondaryText(field)
  const allRules = buildFormRules(required, field.required, customRules)

  // Get current form value to ensure selected value is always in options
  const currentValue = Form.useWatch(formFieldName, form) as CodingValue | undefined

  const questionText = getFieldText(field)
  const questionLinkId = getFieldLinkId(field)

  const sectionReferenceId = getSectionReferenceId(field)
  const shouldDisableUntilGeneratedValue = Boolean(sectionReferenceId && !currentValue)

  const { generatedItemValue } = useGenerateAnswerFromSectionResults({
    questionType: field.type as QuestionnaireAnswerType,
    questionLinkId,
    questionText,
    questionAnswerOptions: field.answerOption
  })

  useEffect(() => {
    if (!form || !triggerAutosave) return
    if (!generatedItemValue || !isValidateFormValuesType(generatedItemValue)) return

    const nextValue = generatedItemValue.value
    const currentFormValue = form.getFieldValue(formFieldName)
    if (isEqual(currentFormValue, nextValue)) return

    form.setFieldValue(formFieldName, nextValue)
    triggerAutosave()
  }, [form, triggerAutosave, generatedItemValue, formFieldName])

  // Merge selected value into options to ensure it's always available for display
  const mergedOptions = useMemo(() => {
    if (!currentValue || typeof currentValue !== 'object' || !('code' in currentValue)) {
      return options
    }

    const code = String(currentValue.code)
    const display = currentValue.display || code

    // Check if the current value is already in options
    const isInOptions = options.some(
      (option) =>
        option.value.toLowerCase() === code.toLowerCase() ||
        option.code.toLowerCase() === code.toLowerCase()
    )

    if (isInOptions) {
      return options
    }

    // Construct full label with ICD code prefix
    const fullLabel = code && display && code !== display ? `${code} - ${display}` : display || code

    // Add the missing selected value to options
    const missingOption: CodingSelectOption = {
      label: fullLabel,
      value: code,
      code: code,
      display: display
    }

    return [...options, missingOption]
  }, [options, currentValue])

  const getValueFromEvent = useCallback(
    (code: string) => createSelectValueFromEvent(mergedOptions)(code),
    [mergedOptions]
  )
  const getValueProps = useCallback(
    (value) => createSelectValueProps(mergedOptions)(value),
    [mergedOptions]
  )

  return (
    <QuestionContainer linkId={field.linkId}>
      <Container>
        <LabelAndDescriptionFieldLayout
          label={field.text}
          secondaryText={secondaryText}
          layout={layout}
        >
          <Form.Item
            name={formFieldName}
            rules={allRules}
            style={{ margin: 0 }}
            getValueFromEvent={getValueFromEvent}
            getValueProps={getValueProps}
          >
            <Select
              disabled={disabled || shouldDisableUntilGeneratedValue}
              placeholder={placeholder}
              options={mergedOptions}
              loading={loading}
              showSearch={showSearch}
              allowClear={allowClear}
              {...selectProps}
            />
          </Form.Item>
        </LabelAndDescriptionFieldLayout>
      </Container>
    </QuestionContainer>
  )
}

const Container = styled.div`
  margin: 12px 0;
`

export default CodingSelectQuestion
